import { z } from "zod";

/**
 * Per-campaign engagement stats for a list (the `list/campaign-stats/:listId` endpoint), used by the
 * copywriting campaign table and the reach-out campaign picker. Counts are SQL aggregates — coerced
 * from MySQL's strings to numbers at the API boundary:
 * - `totalLeads`      — leads assigned to the campaign (the narrow per-campaign `ASSIGNED_LEAD_STATUSES`).
 * - `completed`       — leads whose campaign is no longer running for them: `ENDED_CAMPAIGN_LEAD_STATUSES`
 *                       (ENDED, MESSAGE_SENT, REPLIES, DONE). Built from the same shared predicate as the
 *                       Analytics "Ended Campaigns" KPI so the two surfaces always agree (PREC-3811) —
 *                       it previously counted DONE alone and read far too low.
 * - `contacted`       — assigned leads with at least one conversation `sentAt`.
 * - `positiveReplies` — replied leads whose best conversation sentiment is INTERESTED.
 *
 * Every count spans one population: included, non-blacklisted leads with a live, visible,
 * non-placeholder prospect. The visibility guard is per-user, so these numbers are scoped to the
 * requesting user, exactly as Analytics is.
 */
export const campaignStatSchema = z.object({
	campaignId: z.string(),
	totalLeads: z.coerce.number(),
	completed: z.coerce.number(),
	contacted: z.coerce.number(),
	positiveReplies: z.coerce.number(),
});

export type CampaignStat = z.infer<typeof campaignStatSchema>;

export const campaignStatsResponseSchema = z.object({
	data: z.array(campaignStatSchema),
});

export type CampaignStatsResponse = z.infer<typeof campaignStatsResponseSchema>;
